import React, { useState, useEffect, useRef, useCallback } from 'react';
import { marked } from 'marked';
import { Copy, Check, BookOpen, Zap } from 'lucide-react';
import { AIService } from '../services';
import { checkBackendHealth } from '../utils/healthCheck';
import { formatInsightToChatPrompt } from '../prompts';
import { useCopyToClipboard } from '../hooks/useCopyToClipboard';
import { parseSurfaceDiff } from '../utils/lensParser';
import {
  getCodePositionFromElement,
  getInputCodeContext,
  stitchInsightIntoHTML,
  generateInsightCacheKey,
  isValidInsightToken,
  type InsightData
} from '../utils/codeMapping';

// TypeScript interfaces
interface SyntaxLensWidgetProps {
  children: React.ReactNode;
  className?: string;
  sourceLanguage?: string;
  targetLanguage?: string;
  skipBackendCheck?: boolean;
  variant?: 'classic' | 'modern';
  showBadge?: boolean;
  isStreaming?: boolean;
  code?: string; // NEW: Input code for dynamic insight generation
  generatedHTML?: string; // NEW: Current HTML state for stitching
  onHTMLUpdate?: (html: string) => void; // NEW: Callback to update HTML with dynamic insights
  provider?: any; // NEW: Provider preference for dynamic insights (fast/quality model)
}

// Production-ready SyntaxLens React Widget with AI Integration
const SyntaxLensWidget: React.FC<SyntaxLensWidgetProps> = ({
  children,
  className = "",
  sourceLanguage = "C",
  targetLanguage = "Rust",
  skipBackendCheck = false,
  variant = "classic",
  showBadge = false,
  isStreaming = false,
  code = "", // NEW: Input code
  generatedHTML = "", // NEW: Current HTML
  onHTMLUpdate, // NEW: HTML update callback
  provider // NEW: Provider preference
}) => {
  const [lens, setLens] = useState({
    visible: false,
    pinned: false,
    position: { x: 0, y: 0 },
    pinnedPosition: { x: 0, y: 0 },
    currentDepth: 'surface',
    data: {
      surface: '',
      concept: '',
      paradigm: '',
      badge: '?'
    },
    loading: false
  });

  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [aiService, setAIService] = useState<AIService | null>(null);
  // Initialize state based on skipBackendCheck prop
  const [backendAvailable, setBackendAvailable] = useState<boolean | null>(skipBackendCheck ? true : null); 
  const [backendChecked, setBackendChecked] = useState(skipBackendCheck); 
  const [cursorVisible, setCursorVisible] = useState(false);
  const [activeElement, setActiveElement] = useState<Element | null>(null);
  const [hoverableElements, setHoverableElements] = useState<Element[]>([]);
  const [currentInsightIndex, setCurrentInsightIndex] = useState<number>(-1);
  const [isKeyboardMode, setIsKeyboardMode] = useState(false);
  const [keyboardDepthPreference, setKeyboardDepthPreference] = useState<'surface' | 'concept' | 'paradigm'>('surface');
  const { copied, copy: copyToClipboard } = useCopyToClipboard();
  const containerRef = useRef<HTMLDivElement>(null);
  const lensRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const lastClickTime = useRef(0);

  // NEW: Long-press state for dynamic insight generation
  const [longPressState, setLongPressState] = useState<'idle' | 'pending' | 'generating'>('idle');
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const longPressTarget = useRef<Element | null>(null);
  const longPressTriggered = useRef<boolean>(false); // Track if timeout fired
  const mouseDownTime = useRef<number>(0); // Track when mousedown occurred

  // NEW: Cache for dynamically generated insights
  const [dynamicInsightsCache, setDynamicInsightsCache] = useState<Map<string, InsightData>>(new Map());

  // NEW: Clear cache when language pair changes
  useEffect(() => {
    setDynamicInsightsCache(new Map());
  }, [sourceLanguage, targetLanguage]);

  // Check backend availability - only for dynamic content
  useEffect(() => {
    // Skip backend check for static demos - state is already initialized correctly
    if (skipBackendCheck) {
      return;
    }

    const checkBackend = async () => {
      const available = await checkBackendHealth();
      setBackendAvailable(available);
      setBackendChecked(true);
      if (available) {
        setAIService(new AIService());
      }
    };
    
    // Add a small delay for production to prevent flash
    const isProduction = import.meta.env.PROD;
    const delay = isProduction ? 1000 : 0; // 1 second delay in production
    
    const timer = setTimeout(checkBackend, delay);
    
    // Check every 30 seconds
    const interval = setInterval(checkBackend, 30000);
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [skipBackendCheck]);

  // Update active element highlighting when lens is pinned
  useEffect(() => {
    // Remove active-pinned class from all elements
    if (containerRef.current) {
      const allHoverables = containerRef.current.querySelectorAll('.hoverable');
      allHoverables.forEach(el => el.classList.remove('active-pinned'));
    }

    // Add active-pinned class to the current active element if lens is pinned
    if (lens.pinned && activeElement) {
      activeElement.classList.add('active-pinned');
    }
  }, [lens.pinned, activeElement]);

  // Mouse tracking with throttling
  const handleMouseMove = useCallback((e: MouseEvent) => {
    const newMouse = { x: e.clientX, y: e.clientY };
    setMouse(newMouse);

    // Exit keyboard mode when mouse moves
    if (isKeyboardMode) {
      setIsKeyboardMode(false);
      // Reset depth preference for next keyboard session
      setKeyboardDepthPreference('surface');

      // Re-enable cursor if mouse is inside container
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const isInsideContainer =
          newMouse.x >= rect.left &&
          newMouse.x <= rect.right &&
          newMouse.y >= rect.top &&
          newMouse.y <= rect.bottom;

        if (isInsideContainer) {
          setCursorVisible(true);
        }
      }
    }

    // Update cursor position if visible
    if (cursorRef.current && cursorVisible && !isKeyboardMode) {
      cursorRef.current.style.left = `${newMouse.x}px`;
      cursorRef.current.style.top = `${newMouse.y}px`;
    }

    if (lens.visible && !lens.pinned) {
      setLens(prev => ({
        ...prev,
        position: newMouse
      }));
    }
  }, [lens.visible, lens.pinned, cursorVisible, isKeyboardMode]);

  // Enhanced show lens with AI integration
  const showLens = useCallback(async (element: Element, initialDepth?: 'surface' | 'concept' | 'paradigm') => {
    try {
      const surface = element?.getAttribute('data-surface') || '';
      const concept = element?.getAttribute('data-concept') || '';
      const paradigm = element?.getAttribute('data-paradigm') || '';
      // Compute badge from sourceLanguage prop (first letter uppercase)
      const badge = sourceLanguage.charAt(0).toUpperCase();
      const codeElement = element?.textContent?.trim() || '';

      if (!surface.trim() && !codeElement) return;

      // Track the active element for persistent highlighting
      setActiveElement(element);

      // Update current insight index for keyboard navigation continuity
      const elementIndex = hoverableElements.indexOf(element);
      if (elementIndex !== -1) {
        setCurrentInsightIndex(elementIndex);
      }

      // Show lens immediately with existing data or loading state
      // Use provided initialDepth or default to 'surface'
      setLens(prev => ({
        ...prev,
        visible: true,
        currentDepth: initialDepth || 'surface',
        data: surface ? { surface, concept, paradigm, badge } : { surface: 'Loading...', concept: '', paradigm: '', badge: '?' },
        position: prev.pinned ? prev.pinnedPosition : mouse,
        loading: !surface && !!aiService
      }));

      // If no static data and AI is available, generate explanations
      if (!surface && aiService && codeElement) {
        try {
          const explanation = await aiService.generateExplanation(codeElement, sourceLanguage, targetLanguage);
          
          setLens(prev => ({
            ...prev,
            data: {
              surface: explanation.surface || 'No explanation available',
              concept: explanation.concept || '',
              paradigm: explanation.paradigm || '',
              badge: explanation.badge || sourceLanguage.charAt(0)
            },
            loading: false
          }));
        } catch (error) {
          console.error('Error generating explanation:', error);
          setLens(prev => ({
            ...prev,
            data: {
              surface: 'Error generating explanation',
              concept: '',
              paradigm: '',
              badge: '!'
            },
            loading: false
          }));
        }
      }
    } catch (error) {
      console.warn('SyntaxLens: Error showing lens', error);
    }
  }, [mouse, aiService, sourceLanguage, targetLanguage, hoverableElements]);

  // Hide lens safely
  const hideLens = useCallback(() => {
    if (lens.pinned) return;

    setLens(prev => ({
      ...prev,
      visible: false,
      currentDepth: 'surface'
    }));

    // Clear active element when lens is hidden
    setActiveElement(null);
  }, [lens.pinned]);

  // NEW: Generate dynamic insight for element
  const generateDynamicInsight = useCallback(async (element: Element) => {
    // Validate we have necessary data
    if (!code || !aiService || !generatedHTML || !onHTMLUpdate) {
      return;
    }

    // Validate token is suitable
    if (!isValidInsightToken(element)) {
      return;
    }

    try {
      setLongPressState('generating');

      // Map element to input code position
      const position = getCodePositionFromElement(element, code);
      if (!position) {
        setLongPressState('idle');
        return;
      }

      // Extract context from input code
      const context = getInputCodeContext(code, position, 2); // ±2 lines

      // Check cache first
      const cacheKey = generateInsightCacheKey(context.token, context, sourceLanguage, targetLanguage);
      const cached = dynamicInsightsCache.get(cacheKey);

      if (cached) {
        // Stitch cached insight into HTML
        const updatedHTML = stitchInsightIntoHTML(generatedHTML, position, cached);
        onHTMLUpdate(updatedHTML);

        // Show lens immediately
        setLongPressState('idle');
        setTimeout(() => showLens(element), 100); // Small delay for DOM update
        return;
      }

      // Show loading state in lens
      setLens(prev => ({
        ...prev,
        visible: true,
        loading: true,
        data: {
          surface: 'Generating insight...',
          concept: '',
          paradigm: '',
          badge: sourceLanguage.charAt(0).toUpperCase()
        },
        position: mouse
      }));

      // Generate insight with context and provider preference
      const insight = await aiService.generateExplanation(
        context.token,
        sourceLanguage,
        targetLanguage,
        {
          context: context.fullContext,
          lineNumber: context.lineNumber,
          provider // Pass provider for fast/quality model selection
        }
      );

      const insightData: InsightData = {
        surface: insight.surface || `${sourceLanguage}: ? → ${targetLanguage}: ${context.token}`,
        concept: insight.concept || 'No concept explanation available',
        paradigm: insight.paradigm || 'No paradigm explanation available'
      };

      // Cache the insight
      setDynamicInsightsCache(prev => new Map(prev).set(cacheKey, insightData));

      // Stitch insight into HTML
      const updatedHTML = stitchInsightIntoHTML(generatedHTML, position, insightData);
      onHTMLUpdate(updatedHTML);

      // Update lens with generated insight
      setLens(prev => ({
        ...prev,
        loading: false,
        data: {
          ...insightData,
          badge: sourceLanguage.charAt(0).toUpperCase()
        }
      }));

      // Clear visual feedback
      if (longPressTarget.current) {
        longPressTarget.current.removeAttribute('data-long-press-state');
        (longPressTarget.current as HTMLElement).style.outline = '';
        (longPressTarget.current as HTMLElement).style.outlineOffset = '';
        (longPressTarget.current as HTMLElement).style.backgroundColor = '';
      }

      setLongPressState('idle');

    } catch (error) {
      console.error('[SyntaxLensWidget] Error generating dynamic insight:', error);

      setLens(prev => ({
        ...prev,
        loading: false,
        data: {
          surface: 'Error generating insight',
          concept: 'Please try again',
          paradigm: '',
          badge: '!'
        }
      }));

      // Clear visual feedback
      if (longPressTarget.current) {
        longPressTarget.current.removeAttribute('data-long-press-state');
        (longPressTarget.current as HTMLElement).style.outline = '';
        (longPressTarget.current as HTMLElement).style.outlineOffset = '';
        (longPressTarget.current as HTMLElement).style.backgroundColor = '';
      }

      setLongPressState('idle');
    }
  }, [code, aiService, generatedHTML, onHTMLUpdate, sourceLanguage, targetLanguage, dynamicInsightsCache, mouse, showLens]);

  // Handle depth navigation
  const navigateToDepth = useCallback((depth: string) => {
    setLens(prev => ({
      ...prev,
      currentDepth: depth
    }));
  }, []);

  // Focus guard: Check if focus is on input element to prevent keyboard shortcuts
  const isFocusOnInput = useCallback((): boolean => {
    const activeEl = document.activeElement;
    if (!activeEl) return false;

    const tagName = activeEl.tagName.toLowerCase();
    const isContentEditable = activeEl.getAttribute('contenteditable') === 'true';

    return (
      tagName === 'input' ||
      tagName === 'textarea' ||
      tagName === 'select' ||
      isContentEditable
    );
  }, []);

  // Update hoverable elements list when container changes
  useEffect(() => {
    if (containerRef.current) {
      const elements = Array.from(containerRef.current.querySelectorAll('.hoverable'));
      setHoverableElements(elements);
    }
  }, [children]);

  // Hide cursor when entering keyboard mode
  useEffect(() => {
    if (isKeyboardMode) {
      setCursorVisible(false);
    }
  }, [isKeyboardMode]);

  // Navigate between insights
  const navigateToInsight = useCallback(async (direction: 'next' | 'prev' | 'first' | 'last') => {
    if (hoverableElements.length === 0) return;

    // Enter keyboard mode
    setIsKeyboardMode(true);

    let newIndex = currentInsightIndex;

    // If no element is active yet, start with first element
    if (currentInsightIndex === -1) {
      newIndex = direction === 'prev' || direction === 'last'
        ? hoverableElements.length - 1
        : 0;
    } else {
      switch (direction) {
        case 'next':
          newIndex = currentInsightIndex < hoverableElements.length - 1
            ? currentInsightIndex + 1
            : 0; // Wrap to first
          break;
        case 'prev':
          newIndex = currentInsightIndex > 0
            ? currentInsightIndex - 1
            : hoverableElements.length - 1; // Wrap to last
          break;
        case 'first':
          newIndex = 0;
          break;
        case 'last':
          newIndex = hoverableElements.length - 1;
          break;
      }
    }

    if (newIndex >= 0 && newIndex < hoverableElements.length) {
      const element = hoverableElements[newIndex];
      setCurrentInsightIndex(newIndex);

      // Get element position to position lens circle over it
      const rect = element.getBoundingClientRect();

      // POSITIONING MATH (matching mouse hover behavior):
      // - Lens has transform: translate(-40px, -40px)
      // - Circle is 80px diameter (40px radius)
      // - When pinned: adds p-4 (16px) + border (1px) = 17px offset
      // - For circle center to be at (targetX, targetY):
      //   pinnedPosition must be (targetX - 17, targetY - 17)

      // Calculate element center as target for circle center
      const elementCenterX = rect.left + rect.width / 2;
      const elementCenterY = rect.top + rect.height / 2;

      // Calculate lens position for circle to cover element center
      let lensX = elementCenterX - 17;
      let lensY = elementCenterY - 17;

      // Viewport bounds checking
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Content extends to the right via ml-24 (96px) and is min-w-64 (256px)
      // Total approximate width: 40px (circle) + 96px (gap) + 256px (content) = ~400px
      const lensContentWidth = 400;
      const lensContentHeight = 300; // Approximate

      // Calculate where content would end up
      // Lens visual X = lensX - 17 - 40 = lensX - 57
      // Circle center = lensX - 57 + 40 = lensX - 17
      // Content right edge ≈ (lensX - 17) + 400
      const contentRightEdge = lensX - 17 + lensContentWidth;
      const contentBottomEdge = lensY - 17 + lensContentHeight;

      // If content overflows right, shift lens left
      if (contentRightEdge > viewportWidth - 20) {
        const overflow = contentRightEdge - (viewportWidth - 20);
        lensX -= overflow;
      }

      // If content overflows bottom, shift lens up
      if (contentBottomEdge > viewportHeight - 20) {
        const overflow = contentBottomEdge - (viewportHeight - 20);
        lensY -= overflow;
      }

      // Ensure lens doesn't go off left edge (keep circle visible)
      // Lens visual X should be at least 60px from left (to show full circle)
      const minLensX = 60 + 17; // Visual 60px + compensation
      if (lensX < minLensX) {
        lensX = minLensX;
      }

      // Ensure lens doesn't go off top edge
      const minLensY = 60 + 17;
      if (lensY < minLensY) {
        lensY = minLensY;
      }

      // Show lens for this element first (updates content)
      // Pass keyboard depth preference to showLens to avoid race condition
      await showLens(element, keyboardDepthPreference);

      // Pin the lens with calculated position
      // The -17 will be compensated by pinned styling to keep circle centered
      setLens(prev => ({
        ...prev,
        pinned: true,
        pinnedPosition: {
          x: lensX,
          y: lensY
        }
      }));

      // Scroll element into view smoothly
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    }
  }, [hoverableElements, currentInsightIndex, showLens]);

  // Handle element interactions
  const handleElementClick = useCallback((e: MouseEvent, _element: Element) => {
    try {
      e.preventDefault();
      e.stopPropagation();

      const now = Date.now();
      const timeDiff = now - lastClickTime.current;

      if (timeDiff < 300) {
        // Double click - show paradigm
        if (lens.data.paradigm) {
          navigateToDepth('paradigm');
        }
      } else {
        // Single click - show concept
        if (lens.data.concept) {
          navigateToDepth('concept');
        }
      }

      lastClickTime.current = now;
    } catch (error) {
      console.warn('SyntaxLens: Error handling click', error);
    }
  }, [lens.data, navigateToDepth]);

  // Toggle pin state
  const togglePin = useCallback((e: Event) => {
    try {
      e.preventDefault();
      e.stopPropagation();

      if (!lens.visible) return;

      setLens(prev => ({
        ...prev,
        pinned: !prev.pinned,
        // When pinning:  17px = 16px padding + 1px border - the exact amount of visual offset introduced
        //when the lens switches to pinned state.
        pinnedPosition: prev.pinned 
          ? { x: 0, y: 0 } 
          : { 
              x: mouse.x - 17, 
              y: mouse.y - 17 
            }
      }));
      
      // Clear active element when unpinning
      if (lens.pinned) {
        setActiveElement(null);
      }
    } catch (error) {
      console.warn('SyntaxLens: Error toggling pin', error);
    }
  }, [lens.visible, mouse]);

  // Click outside handler
  const handleDocumentClick = useCallback((e: Event) => {
    try {
      // Exit keyboard mode on any click
      if (isKeyboardMode && e instanceof MouseEvent) {
        setIsKeyboardMode(false);
        // Reset depth preference for next keyboard session
        setKeyboardDepthPreference('surface');

        // Re-enable cursor if click is inside container
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          const isInsideContainer =
            e.clientX >= rect.left &&
            e.clientX <= rect.right &&
            e.clientY >= rect.top &&
            e.clientY <= rect.bottom;

          if (isInsideContainer) {
            setCursorVisible(true);
          }
        }
      }

      // Unpin when clicking outside lens (both left and right click)
      if (lens.pinned && lensRef.current && e.target instanceof Node && !lensRef.current.contains(e.target)) {
        const isHoverableElement = (e.target as Element).closest?.('.hoverable');
        if (!isHoverableElement) {
          setLens(prev => ({ ...prev, pinned: false, visible: false }));
          setActiveElement(null);
        }
      }
    } catch (error) {
      console.warn('SyntaxLens: Error handling document click', error);
    }
  }, [lens.pinned, isKeyboardMode]);

  // Right-click handler - unpin when lens is pinned
  const handleDocumentContextMenu = useCallback((e: MouseEvent) => {
    try {
      // If lens is pinned, right-click anywhere unpins it
      if (lens.pinned) {
        e.preventDefault(); // Prevent browser context menu

        // Check if right-click is on the lens itself
        if (lensRef.current && e.target instanceof Node && lensRef.current.contains(e.target)) {
          // Right-click on lens - unpin
          setLens(prev => ({ ...prev, pinned: false, visible: false }));
          setActiveElement(null);
        } else {
          // Right-click outside lens - also unpin
          setLens(prev => ({ ...prev, pinned: false, visible: false }));
          setActiveElement(null);
        }
      }
    } catch (error) {
      console.warn('SyntaxLens: Error handling context menu', error);
    }
  }, [lens.pinned]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    try {
      // Focus guard: Don't handle shortcuts if focus is on input element
      if (isFocusOnInput()) return;

      // Navigation between insights (works even without lens visible)
      if (e.key === 'ArrowRight' || e.key === 'j') {
        e.preventDefault();
        navigateToInsight('next');
        return;
      }

      if (e.key === 'ArrowLeft' || e.key === 'k') {
        e.preventDefault();
        navigateToInsight('prev');
        return;
      }

      if (e.key === 'Home') {
        e.preventDefault();
        navigateToInsight('first');
        return;
      }

      if (e.key === 'End') {
        e.preventDefault();
        navigateToInsight('last');
        return;
      }

      // Space to pin/unpin (only when lens is visible)
      if (e.key === ' ' && lens.visible) {
        e.preventDefault();
        togglePin(e);
        return;
      }

      // Rest of the shortcuts require lens to be visible
      if (!lens.visible) return;

      switch(e.key) {
        case '1':
          e.preventDefault();
          navigateToDepth('surface');
          // Update keyboard depth preference
          if (isKeyboardMode) {
            setKeyboardDepthPreference('surface');
          }
          break;
        case '2':
          e.preventDefault();
          if (lens.data.concept) {
            navigateToDepth('concept');
            // Update keyboard depth preference
            if (isKeyboardMode) {
              setKeyboardDepthPreference('concept');
            }
          }
          break;
        case '3':
          e.preventDefault();
          if (lens.data.paradigm) {
            navigateToDepth('paradigm');
            // Update keyboard depth preference
            if (isKeyboardMode) {
              setKeyboardDepthPreference('paradigm');
            }
          }
          break;
        case 'Escape':
          e.preventDefault();
          // Exit keyboard mode
          if (isKeyboardMode) {
            setIsKeyboardMode(false);
            // Reset depth preference for next keyboard session
            setKeyboardDepthPreference('surface');

            // Re-enable cursor if mouse is inside container
            if (containerRef.current && mouse) {
              const rect = containerRef.current.getBoundingClientRect();
              const isInsideContainer =
                mouse.x >= rect.left &&
                mouse.x <= rect.right &&
                mouse.y >= rect.top &&
                mouse.y <= rect.bottom;

              if (isInsideContainer) {
                setCursorVisible(true);
              }
            }
          }
          if (lens.pinned) {
            setLens(prev => ({ ...prev, pinned: false, visible: false }));
          }
          break;
      }
    } catch (error) {
      console.warn('SyntaxLens: Error handling keydown', error);
    }
  }, [lens.visible, lens.data, lens.pinned, navigateToDepth, navigateToInsight, togglePin, isFocusOnInput]);

  // Handle container mouse enter/leave for cursor
  const handleContainerMouseEnter = useCallback(() => {
    // Don't show cursor in keyboard mode
    if (!isKeyboardMode) {
      setCursorVisible(true);
    }
  }, [isKeyboardMode]);

  const handleContainerMouseLeave = useCallback(() => {
    setCursorVisible(false);
  }, []);

  // Setup event listeners with cleanup
  useEffect(() => {
    const cleanup: (() => void)[] = [];

    // Document event listeners
    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('contextmenu', handleDocumentContextMenu);
    document.addEventListener('keydown', handleKeyDown);

    cleanup.push(() => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('click', handleDocumentClick);
      document.removeEventListener('contextmenu', handleDocumentContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    });

    // Container mouse enter/leave for cursor
    if (containerRef.current) {
      const container = containerRef.current;
      container.addEventListener('mouseenter', handleContainerMouseEnter);
      container.addEventListener('mouseleave', handleContainerMouseLeave);

      cleanup.push(() => {
        container.removeEventListener('mouseenter', handleContainerMouseEnter);
        container.removeEventListener('mouseleave', handleContainerMouseLeave);
      });

      // NEW: Long-press event delegation (container-level for all tokens)
      const handleContainerMouseDown = (e: MouseEvent) => {
        mouseDownTime.current = Date.now();

        // Only enable long-press if we have necessary props
        if (!code || !generatedHTML || !onHTMLUpdate) {
          return;
        }

        // Find the actual span element (token)
        const target = (e.target as Element).closest('span[class*="text-"]');
        if (!target || !isValidInsightToken(target)) {
          return;
        }

        // Stop event from reaching other handlers
        e.stopPropagation();

        // Start long-press timer
        longPressTarget.current = target;
        longPressTriggered.current = false; // Reset flag
        setLongPressState('pending');

        // Apply visual feedback immediately with animation
        target.setAttribute('data-long-press-state', 'pending');
        (target as HTMLElement).style.outline = '2px dashed rgba(251, 146, 60, 0.6)';
        (target as HTMLElement).style.outlineOffset = '2px';
        (target as HTMLElement).style.transition = 'background-color 300ms linear';
        (target as HTMLElement).style.backgroundColor = 'rgba(251, 146, 60, 0.3)';

        // Clear any existing timer first
        if (longPressTimer.current) {
          clearTimeout(longPressTimer.current);
        }

        longPressTimer.current = setTimeout(() => {
          if (longPressTarget.current) {
            longPressTriggered.current = true; // Mark as triggered
            setLongPressState('generating');

            // Update visual feedback
            (longPressTarget.current as HTMLElement).style.outline = '2px solid rgba(251, 146, 60, 0.9)';
            (longPressTarget.current as HTMLElement).style.backgroundColor = 'rgba(251, 146, 60, 0.1)';

            generateDynamicInsight(longPressTarget.current);
          }
        }, 300); // 300ms threshold (reduced from 500ms for better UX)
      };

      const handleContainerMouseUp = (e: MouseEvent) => {
        // Only cancel if long-press hasn't triggered yet AND we have an active timer
        if (!longPressTriggered.current && longPressTimer.current) {

          // Clear visual feedback from target
          if (longPressTarget.current) {
            longPressTarget.current.removeAttribute('data-long-press-state');
            (longPressTarget.current as HTMLElement).style.outline = '';
            (longPressTarget.current as HTMLElement).style.outlineOffset = '';
            (longPressTarget.current as HTMLElement).style.backgroundColor = '';
            (longPressTarget.current as HTMLElement).style.transition = '';
          }

          // Cancel the timer
          clearTimeout(longPressTimer.current);
          longPressTimer.current = null;

          longPressTarget.current = null;
          setLongPressState('idle');
        }
      };

      container.addEventListener('mousedown', handleContainerMouseDown);
      document.addEventListener('mouseup', handleContainerMouseUp);

      cleanup.push(() => {
        container.removeEventListener('mousedown', handleContainerMouseDown);
        document.removeEventListener('mouseup', handleContainerMouseUp);
      });
    }

    // Element event listeners - find all hoverable elements in container
    if (containerRef.current) {
      const elements = containerRef.current.querySelectorAll('.hoverable');
      
      elements.forEach(element => {
        const handleMouseEnter = () => {
          // Disable hover interactions in keyboard mode or during streaming
          if (isKeyboardMode || isStreaming) return;
          if (!lens.pinned) showLens(element);
        };

        const handleMouseLeave = () => {
          // Disable hover interactions in keyboard mode or during streaming
          if (isKeyboardMode || isStreaming) return;
          if (!lens.pinned) hideLens();
        };
        
        const handleClick = (e: Event) => handleElementClick(e as MouseEvent, element);
        const handleContextMenu = (e: Event) => togglePin(e);

        element.addEventListener('mouseenter', handleMouseEnter);
        element.addEventListener('mouseleave', handleMouseLeave);
        element.addEventListener('click', handleClick);
        element.addEventListener('contextmenu', handleContextMenu);

        cleanup.push(() => {
          element.removeEventListener('mouseenter', handleMouseEnter);
          element.removeEventListener('mouseleave', handleMouseLeave);
          element.removeEventListener('click', handleClick);
          element.removeEventListener('contextmenu', handleContextMenu);
        });
      });
    }

    return () => {
      cleanup.forEach(fn => fn());
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleMouseMove, handleDocumentClick, handleDocumentContextMenu, handleKeyDown, lens.pinned, showLens, hideLens, handleElementClick, togglePin, handleContainerMouseEnter, handleContainerMouseLeave, isKeyboardMode, isStreaming, code, generatedHTML, onHTMLUpdate, generateDynamicInsight]); // Removed longPressState to prevent cleanup during active long-press

  const shouldShowLevel = (level: string) => {
    switch(level) {
      case 'surface': return true;
      case 'concept': return lens.currentDepth === 'concept' || lens.currentDepth === 'paradigm';
      case 'paradigm': return lens.currentDepth === 'paradigm';
      default: return false;
    }
  };

  // Format explanation text using markdown
  const formatExplanation = useCallback((text: string): string => {
    if (!text) return '';

    try {
      // Configure marked renderer for custom styling
      const renderer = new marked.Renderer();

      // Inline code: `code` → styled code element
      renderer.codespan = (token: any) => {
        const code = typeof token === 'string' ? token : token.text;
        return `<code class="font-mono text-sm bg-gray-700/50 text-blue-300 px-1.5 py-0.5 rounded">${code}</code>`;
      };

      // Strong: **text** → bold emphasis
      renderer.strong = (token: any) => {
        const content = typeof token === 'string' ? token : token.text;
        return `<strong class="font-semibold text-white">${content}</strong>`;
      };

      // Emphasis: *text* → italic terminology
      renderer.em = (token: any) => {
        const content = typeof token === 'string' ? token : token.text;
        return `<em class="text-gray-200 italic">${content}</em>`;
      };

      // Parse inline markdown (no block elements)
      return marked.parseInline(text, {
        renderer,
        breaks: false,
        gfm: true
      }) as string;
    } catch (error) {
      console.warn('Markdown parsing error:', error);
      // Fallback to raw text if parsing fails
      return text;
    }
  }, []);

  // Copy lens content to clipboard
  const handleCopyLens = useCallback(async (e: React.MouseEvent) => {
    try {
      // Prevent event from bubbling up and closing the lens
      e.stopPropagation();
      e.preventDefault();

      const codeElement = activeElement?.textContent?.trim() || '(code element)';
      const parsed = parseSurfaceDiff(lens.data.surface);

      const prompt = formatInsightToChatPrompt({
        codeElement,
        sourceLanguage,
        targetLanguage,
        source: parsed.source || lens.data.surface || '',
        target: parsed.target || '',
        concept: shouldShowLevel('concept') ? lens.data.concept : undefined,
        paradigm: shouldShowLevel('paradigm') ? lens.data.paradigm : undefined,
      });

      await copyToClipboard(prompt);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  }, [activeElement, lens.data, sourceLanguage, targetLanguage, shouldShowLevel, copyToClipboard]);

  const lensPosition = lens.pinned ? lens.pinnedPosition : lens.position;

  // Classic Lens Renderer (Original Design with Cursor Circles)
  const renderClassicLens = () => (
    <div
      ref={lensRef}
      data-testid="syntax-lens"
      className={`fixed z-50 pointer-events-none transform -translate-x-10 -translate-y-10 ${
        lens.pinned ? 'pointer-events-auto' : ''
      }`}
      style={{
        left: `${lensPosition.x}px`,
        top: `${lensPosition.y}px`,
        transition: lens.pinned ? 'none' : 'opacity 0.2s ease'
      }}
    >
      <div className={`relative ${lens.pinned ? 'rounded-xl p-4 shadow-2xl border border-gray-700/50' : ''}`}
           style={lens.pinned ? {
             background: 'linear-gradient(332deg, rgba(31, 41, 55, 1) 0%, rgba(22,26,39,0.96) 60%, rgba(22,26,39,0.60) 70%, rgba(66, 66, 66, 0) 100%)',
             backdropFilter: 'none',
             outline: '1px solid rgba(255,255,255,.06)'
           } : {}}>

        {/* Lens Circle */}
        <div className="relative flex items-center gap-5 mb-2">
          <div className="w-20 h-20 rounded-full border border-blue-400/60 relative opacity-30">
            <div className="absolute -inset-9 rounded-full border border-blue-400/40" />
            {showBadge && (
              <div className="lens-badge absolute -top-11 -right-11 w-7 h-7 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full border-2 border-white shadow-lg" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
                <span style={{ display: 'block', width: '100%', textAlign: 'center', color: 'white', fontSize: '12px', fontWeight: 'bold', lineHeight: '1' }}>
                  {lens.loading ? '⏳' : lens.data.badge}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Content Pills */}
        <div className="flex flex-col ml-24 -mt-16 overflow-hidden rounded-xl border border-white/30 backdrop-blur-sm shadow-2xl">
          {/* Surface Level - Side-by-side Transformation */}
          <div className="min-w-64 max-w-2xl bg-gray-800 px-5 py-5 relative border-b border-white/10">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500" />

            {/* Copy Button - Inline in top-right of surface pill */}
            {lens.pinned && (
              <div className="absolute top-3 right-3 group/copy">
                <button
                  onClick={handleCopyLens}
                  className="w-8 h-8 p-2 bg-gray-800/50 hover:bg-gray-700/70 border border-gray-600/40 hover:border-gray-500/60 rounded-lg flex items-center justify-center text-gray-400 hover:text-white transition-all duration-200 pointer-events-auto"
                  style={{ minWidth: '32px', minHeight: '32px' }}
                  aria-label="Copy snippet"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-green-400 animate-in zoom-in duration-200" />
                  ) : (
                    <Copy className="w-4 h-4 group-hover/copy:scale-105 transition-transform duration-150" />
                  )}
                </button>
                {/* Bottom tooltip - only show when not copied */}
                {!copied && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover/copy:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50 border border-gray-700">
                    Copy
                  </div>
                )}
              </div>
            )}
            {lens.loading ? (
              <span className="font-bold text-blue-400 flex items-center">
                🔹
                <span className="ml-1 text-sm leading-snug">Generating explanation...</span>
              </span>
            ) : (() => {
              const parsed = parseSurfaceDiff(lens.data.surface);
              if (parsed.source && parsed.target) {
                return (
                  <div className="space-y-3">
                    {/* Nearly Invisible Header Row */}
                    <div className="flex items-center gap-4 pb-1.5 border-b border-white/[0.03]">
                      <div className="flex-1 text-[10px] font-normal" style={{ color: '#5a5f69' }}>
                        {sourceLanguage}
                      </div>

                      <div className="flex-1 text-[10px] font-normal" style={{ color: '#5a5f69' }}>
                        {targetLanguage}
                      </div>
                    </div>

                    {/* Code Comparison */}
                    <div className="flex items-start gap-4 pt-1">
                      <code className={`flex-1 text-base font-mono block break-words ${
                        parsed.sourceIsNone
                          ? 'italic text-gray-400/60 font-normal'
                          : 'font-semibold text-blue-200'
                      }`}>
                        {parsed.source}
                      </code>

                      <div className="flex items-center justify-center flex-shrink-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-orange-400">
                          <path d="M4 12H20M20 12L14 6M20 12L14 18"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"/>
                        </svg>
                      </div>

                      <code className={`flex-1 text-base font-mono block break-words ${
                        parsed.targetIsNone
                          ? 'italic text-gray-400/60 font-normal'
                          : 'font-semibold text-orange-200'
                      }`}>
                        {parsed.target}
                      </code>
                    </div>
                  </div>
                );
              } else {
                // Fallback to original format
                return (
                  <span className="font-bold text-blue-400 flex items-center">
                    🔹
                    <span className="ml-1 text-sm leading-snug">{lens.data.surface}</span>
                  </span>
                );
              }
            })()}
          </div>

          {/* Concept Level - Contrast with Bold Terms */}
          {shouldShowLevel('concept') && lens.data.concept && (
            <div className={`min-w-64 max-w-2xl bg-gray-800 px-5 py-5 relative border-b border-white/10 transition-all duration-300 transform ${
              lens.pinned ? 'cursor-pointer hover:bg-gray-700/90' : ''
            } ${shouldShowLevel('concept') ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'}`}>
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-purple-500" />
              <div className="text-sm text-gray-200 flex items-start leading-relaxed">
                <BookOpen className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                <span
                  className="flex-1"
                  dangerouslySetInnerHTML={{ __html: formatExplanation(lens.data.concept) }}
                />
              </div>
            </div>
          )}

          {/* Paradigm Level - Deeper Insight with Bold Terms */}
          {shouldShowLevel('paradigm') && lens.data.paradigm && (
            <div className={`min-w-64 max-w-2xl bg-gray-800 px-5 py-5 relative transition-all duration-300 transform ${
              lens.pinned ? 'cursor-pointer hover:bg-gray-700/85' : ''
            } ${shouldShowLevel('paradigm') ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'}`}>
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-orange-500" />
              <div className="text-sm text-gray-300/80 flex items-start leading-relaxed">
                <Zap className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                <span
                  className="flex-1"
                  dangerouslySetInnerHTML={{ __html: formatExplanation(lens.data.paradigm) }}
                />
              </div>
            </div>
          )}

          {/* Hints - Inside lens window at bottom */}
          <div className="min-w-64 max-w-2xl bg-gray-800 px-5 py-2 relative text-center">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-gray-600" />
            <div className="text-xs text-gray-400">
              {lens.pinned ? (
                'Right-click to unpin'
              ) : lens.currentDepth === 'paradigm' ? (
                '' // No hint at deepest level
              ) : lens.currentDepth === 'concept' ? (
                'Click once more for paradigm ▸'
              ) : (
                'Click once more to see concept▸'
              )}
            </div>
          </div>
        </div>

          {/* Pin Indicator */}
          {lens.pinned && (
            <div className="lens-pin absolute -top-4 -left-4 w-6 h-6 bg-gray-900 rounded-full flex items-center justify-center text-white text-xs  border-white shadow-lg" style={{ justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
              📌
            </div>
          )}

      </div>
    </div>
  );

  // Modern Lens Renderer (Clean Card Design)
  const renderModernLens = () => (
    <div
      ref={lensRef}
      data-testid="syntax-lens"
      className={`fixed z-50 pointer-events-none transform -translate-x-2 -translate-y-2 ${
        lens.pinned ? 'pointer-events-auto' : ''
      } fade-in`}
      style={{
        left: `${lensPosition.x}px`,
        top: `${lensPosition.y}px`,
        transition: lens.pinned ? 'none' : 'opacity 0.2s ease'
      }}
    >
      <div className="bg-gray-900 rounded-xl p-4 shadow-2xl border border-gray-700/50 min-w-72 max-w-md">
        {/* Header with Badge and Copy Button */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
            {lens.loading ? '⏳' : lens.data.badge}
          </div>
          <div className="text-white/70 text-sm font-medium">
            {sourceLanguage} → {targetLanguage}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {lens.pinned && (
              <>
                <div className="relative group/copy">
                  <button
                    onClick={handleCopyLens}
                    className="w-8 h-8 p-2 bg-gray-800/30 hover:bg-gray-700/50 border border-gray-600/40 hover:border-gray-500/60 rounded-lg flex items-center justify-center text-gray-400 hover:text-white transition-all duration-200"
                    style={{ minWidth: '32px', minHeight: '32px' }}
                    aria-label="Copy snippet"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-green-400 animate-in zoom-in duration-200" />
                    ) : (
                      <Copy className="w-4 h-4 group-hover/copy:scale-105 transition-transform duration-150" />
                    )}
                  </button>
                  {/* Bottom tooltip - only show when not copied */}
                  {!copied && (
                    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover/copy:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50 border border-gray-700">
                      Copy
                    </div>
                  )}
                </div>
                <div className="w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs">
                  📌
                </div>
              </>
            )}
          </div>
        </div>

        {/* Content Levels */}
        <div className="space-y-4">
          {/* Surface Level - Side-by-side Transformation */}
          {lens.loading ? (
            <div className="flex items-center mb-2">
              <span className="font-bold text-blue-400 flex items-center">
                🔹
                <span className="ml-1 text-sm leading-snug">Generating explanation...</span>
              </span>
            </div>
          ) : (() => {
            const parsed = parseSurfaceDiff(lens.data.surface);
            if (parsed.source && parsed.target) {
              return (
                <div className="space-y-2 pb-2 border-b border-gray-700/50">
                  <div className="flex items-baseline gap-3">
                    <div className="flex-1">
                      <div className="text-[10px] uppercase tracking-wide text-blue-400/60 mb-1.5">
                        {sourceLanguage} you know
                      </div>
                      <code className={`text-sm font-mono ${
                        parsed.sourceIsNone
                          ? 'italic text-gray-400/60 font-normal'
                          : 'font-medium text-blue-300'
                      }`}>
                        {parsed.source}
                      </code>
                    </div>

                    <div className="flex items-center justify-center px-2">
                      <svg width="20" height="18" viewBox="0 0 24 20" fill="none" className="text-orange-400">
                        <path d="M2 10H22M22 10L16 4M22 10L16 16"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"/>
                      </svg>
                    </div>

                    <div className="flex-1">
                      <div className="text-[10px] uppercase tracking-wide text-orange-400/60 mb-1.5">
                        {targetLanguage} you're learning
                      </div>
                      <code className={`text-sm font-mono ${
                        parsed.targetIsNone
                          ? 'italic text-gray-400/60 font-normal'
                          : 'font-medium text-orange-300'
                      }`}>
                        {parsed.target}
                      </code>
                    </div>
                  </div>
                </div>
              );
            } else {
              return (
                <div className="flex items-center mb-2 pb-2 border-b border-gray-700/50">
                  <span className="font-bold text-blue-400 flex items-center">
                    🔹
                    <span className="ml-1 text-sm leading-snug">{lens.data.surface}</span>
                  </span>
                </div>
              );
            }
          })()}

          {/* Concept Level - Contrast with Bold Terms */}
          {shouldShowLevel('concept') && lens.data.concept && (
            <div className={`transition-all duration-300 ${
              shouldShowLevel('concept') ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
            }`}>
              <div className="text-sm text-gray-200 flex items-start leading-relaxed">
                <BookOpen className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                <span
                  className="flex-1"
                  dangerouslySetInnerHTML={{ __html: formatExplanation(lens.data.concept) }}
                />
              </div>
            </div>
          )}

          {/* Paradigm Level - Deeper Insight with Bold Terms */}
          {shouldShowLevel('paradigm') && lens.data.paradigm && (
            <div className={`transition-all duration-300 ${
              shouldShowLevel('paradigm') ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
            }`}>
              <div className="text-sm italic text-green-300 flex items-start leading-relaxed">
                <Zap className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                <span
                  className="flex-1"
                  dangerouslySetInnerHTML={{ __html: formatExplanation(lens.data.paradigm) }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-4 pt-3 border-t border-gray-700/50 flex items-center justify-between text-xs text-gray-400">
          <div className="flex items-center gap-2">
            <div className={`w-1.5 h-1.5 rounded-full ${shouldShowLevel('surface') ? 'bg-blue-400' : 'bg-gray-600'}`} />
            <div className={`w-1.5 h-1.5 rounded-full ${shouldShowLevel('concept') ? 'bg-purple-400' : 'bg-gray-600'}`} />
            <div className={`w-1.5 h-1.5 rounded-full ${shouldShowLevel('paradigm') ? 'bg-orange-400' : 'bg-gray-600'}`} />
          </div>
          <div className="flex items-center gap-2">
            <div>
              {lens.pinned ? 'Right-click to unpin' : 'Click to pin'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div ref={containerRef} className={`syntax-lens-container ${className} ${lens.visible ? 'lens-active' : ''}`}>
      <style dangerouslySetInnerHTML={{
        __html: `
          /* Custom lens cursor for interactive code area */
          .syntax-lens-container {
            position: relative;
          }
          
          /* Animation for modern lens */
          .fade-in {
            animation: fadeInUp 0.3s ease-out;
          }
          
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          /* Modern variant inline code styling */
          .syntax-lens-container .fade-in code {
            background: #374151 !important;
            padding: 2px 4px !important;
            border-radius: 4px !important;
            font-size: 0.75rem !important;
            color: #93c5fd !important;
            margin: 0 2px !important;
          }
          
          .syntax-lens-cursor {
            position: fixed;
            width: 36px;
            height: 36px;
            border: 1px solid rgba(59, 130, 246, 0.5);
            border-radius: 50%;
            pointer-events: none;
            z-index: 9999;
            transform: translate(-50%, -50%);
          }
          
          .syntax-lens-cursor::after {
            content: '';
            position: absolute;
            width: 4px;
            height: 4px;
            background: rgba(59, 130, 246, 0.6);
            border-radius: 50%;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
          }
          
          .syntax-lens-cursor.hidden {
            opacity: 0;
          }
          
          /* Hide default cursor in code area */
          .syntax-lens-container .font-mono {
            cursor: none;
          }
          
          .syntax-lens-container .hoverable {
            cursor: none;
          }
          
          /* Regular syntax highlighting for all tokens */
          .keyword { color: #ff6b6b !important; font-weight: 600; }
          .type-keyword { color: #4ecdc4 !important; font-weight: 500; }
          .function, .method { color: #45b7d1 !important; font-weight: 500; }
          .variable { color: #ecf0f1 !important; }
          .operator { color: #a8e6cf !important; font-weight: 500; }
          .string { color: #feca57 !important; }
          .number { color: #ff9ff3 !important; }
          .comment { color: #95a5a6 !important; font-style: italic; }
          .bracket { color: #ffa726 !important; font-weight: 600; }
          
          /* Enhanced highlighting for hoverable elements */
          .keyword.hoverable { 
            color: #ff6b6b !important; 
            font-weight: 700; 
            text-shadow: 0 0 2px rgba(255, 107, 107, 0.3);
          }
          .type-keyword.hoverable { 
            color: #4ecdc4 !important; 
            font-weight: 600; 
            text-shadow: 0 0 2px rgba(78, 205, 196, 0.3);
          }
          .function.hoverable, .method.hoverable { 
            color: #45b7d1 !important; 
            font-weight: 600; 
            text-shadow: 0 0 2px rgba(69, 183, 209, 0.3);
          }
          .variable.hoverable { 
            color: #ecf0f1 !important; 
            font-weight: 500;
            text-shadow: 0 0 2px rgba(236, 240, 241, 0.3);
          }
          .operator.hoverable { 
            color: #a8e6cf !important; 
            font-weight: 600; 
            text-shadow: 0 0 2px rgba(168, 230, 207, 0.3);
          }
          .string.hoverable { 
            color: #feca57 !important; 
            font-weight: 500;
            text-shadow: 0 0 2px rgba(254, 202, 87, 0.3);
          }
          .number.hoverable { 
            color: #ff9ff3 !important; 
            font-weight: 500;
            text-shadow: 0 0 2px rgba(255, 159, 243, 0.3);
          }
          .comment.hoverable { 
            color: #95a5a6 !important; 
            font-style: italic; 
            font-weight: 500;
            text-shadow: 0 0 2px rgba(149, 165, 166, 0.3);
          }
          .bracket.hoverable { 
            color: #ffa726 !important; 
            font-weight: 700; 
            text-shadow: 0 0 2px rgba(255, 167, 38, 0.3);
          }
          
  .hoverable {
    display: inline-block;
    background: rgba(255, 255, 255, 0.09);
    border-radius: 3px;
    padding: 1px 3px;
    margin: -1px -3px;
    transition: background-color 0.2s ease, color 0.2s ease;
  }
          
          .hoverable:hover {
            background: rgba(59, 130, 246, 0.4) !important;
            border-radius: 4px;
            padding: 2px 4px;
            margin: -2px -4px;
            filter: brightness(1.3);
            box-shadow: 0 0 8px rgba(59, 130, 246, 0.5);
          }
          
          /* Persistent highlight for pinned active element */
          .hoverable.active-pinned {
            background: rgba(59, 130, 246, 0.6) !important;
            border-radius: 4px;
            padding: 2px 4px;
            margin: -2px -4px;
            filter: brightness(1.4);
            box-shadow: 0 0 12px rgba(59, 130, 246, 0.7);
            outline: 2px solid rgba(59, 130, 246, 0.8);
            outline-offset: 1px;
          }
          
          /* Force left alignment for all code content */
          .syntax-lens-container .font-mono,
          .syntax-lens-container .text-left {
            text-align: left !important;
            justify-content: flex-start !important;
          }
          
          .syntax-lens-container .flex.items-center {
            justify-content: flex-start !important;
            align-items: center !important;
            min-height: 24px;
          }
          
          /* Force left alignment for concept and paradigm pills */
          .syntax-lens-container [class*="bg-gray-800"] {
            text-align: left !important;
          }
          
          /* Override for lens badge to center properly - more specific selector */
          .syntax-lens-container .lens-badge.flex.items-center {
            justify-content: center !important;
            text-align: center !important;
          }
          
          .lens-badge span {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            text-align: center !important;
          }
          
          /* Override for lens pin to center properly - more specific selector */
          .syntax-lens-container .lens-pin.flex.items-center {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            text-align: center !important;
          }
          
          .syntax-lens-container .space-y-2 > * {
            margin-left: 0 !important;
            margin-right: auto !important;
            justify-content: flex-start !important;
          }
          
          /* Prevent line height changes on hover */
          .syntax-lens-container .flex-1 {
            line-height: 1.5 !important;
          }
          
          .syntax-lens-container .hoverable {
            vertical-align: baseline !important;
          }
          
          /* Disable text selection when lens is active */
          .syntax-lens-container${lens.visible ? '.lens-active' : ''} .hoverable {
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            user-select: none !important;
          }
          
          /* Disable text selection for the entire code container when lens is visible */
          .syntax-lens-container${lens.visible ? '.lens-active' : ''} {
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            user-select: none !important;
          }
        `
      }} />
      
      {children}
      
      {/* Custom Lens Cursor - Hidden in keyboard mode */}
      <div
        ref={cursorRef}
        className={`syntax-lens-cursor ${cursorVisible && !isKeyboardMode ? '' : 'hidden'}`}
        style={{
          left: `${mouse.x}px`,
          top: `${mouse.y}px`
        }}
      />
      
      {/* Backend Status Banner - Only show if we've checked and backend is unavailable */}
      {backendChecked && backendAvailable === false && (
        <div className="fixed top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg z-50">
          <div className="text-sm">
            <strong>Backend Server Required</strong>
            <br />
            <span className="text-xs">
              Start the backend server: <code>npm run start:backend</code>
            </span>
          </div>
        </div>
      )}

      
      {/* Lens Widget */}
      {lens.visible && (
        variant === "classic" ? renderClassicLens() : renderModernLens()
      )}
    </div>
  );
};

export { SyntaxLensWidget };
export default SyntaxLensWidget;